// ===================================================================
// PA GRANTS — Project Photos Gallery
// Bulk upload, thumbnail gallery with lightbox, phase tagging
// ===================================================================

import { useState, useRef } from 'react';
import {
  Paper, Text, Group, Stack, Badge, Button, ActionIcon, Modal,
  SimpleGrid, Loader, Image, Textarea, Select,
} from '../../components/gab/GabComponents';
import {
  IconUpload, IconTrash, IconPhoto, IconMaximize, IconMap, IconEdit,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { createRecord, updateRecord } from '../../api/client';
import { uploadFileToV2, getV2FileDownloadUrl } from '../../lib/uploadFile';
import { useAuthStore } from '../../store/authStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { fmtDateTime } from '../../lib/date-format';
import type { PADocument } from '../../types/pagrants';

// ── Phase config ──────────────────────────────────────────────

export const PHOTO_PHASES: { value: string; label: string; color: string }[] = [
  { value: 'pre_disaster',  label: 'Pre-Disaster',      color: '#1d4ed8' },
  { value: 'during_event',  label: 'During Event',       color: '#b45309' },
  { value: 'post_disaster', label: 'Post-Disaster',      color: '#b91c1c' },
  { value: 'during_work',   label: 'During Work',        color: '#6d28d9' },
  { value: 'post_work',     label: 'Post-Work',          color: '#15803d' },
  { value: 'damage_photos', label: 'General',            color: '#64748b' },
];

const PHASE_MAP = Object.fromEntries(PHOTO_PHASES.map(p => [p.value, p]));

const PHASE_SELECT_OPTIONS = PHOTO_PHASES.map(p => ({ value: p.value, label: p.label }));

function PhaseBadge({ tag }: { tag: string }) {
  const phase = PHASE_MAP[tag];
  if (!phase) return null;
  return (
    <Badge size="xs" style={{ background: phase.color + '22', color: phase.color, border: `1px solid ${phase.color}44` }}>
      {phase.label}
    </Badge>
  );
}

// ── Props ─────────────────────────────────────────────────────

interface ProjectPhotosProps {
  projectId: string;
  organizationId: string;
  documents: PADocument[];
}

// ── Image helpers ─────────────────────────────────────────────

const isImage = (mime: string) =>
  mime.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(mime);

function createGridThumb(file: File): Promise<string> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const maxSize = 200;
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > maxSize || h > maxSize) {
        const ratio = Math.min(maxSize / w, maxSize / h);
        w = Math.round(w * ratio); h = Math.round(h * ratio);
      }
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.55));
    };
    img.onerror = () => resolve('');
    img.src = URL.createObjectURL(file);
  });
}

function createLightboxImage(file: File): Promise<string> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const maxSize = 1200;
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > maxSize || h > maxSize) {
        const ratio = Math.min(maxSize / w, maxSize / h);
        w = Math.round(w * ratio); h = Math.round(h * ratio);
      }
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => resolve('');
    img.src = URL.createObjectURL(file);
  });
}

// ── Component ─────────────────────────────────────────────────

export default function ProjectPhotos({ projectId, organizationId, documents }: ProjectPhotosProps) {
  const { fetchDocuments, deleteDocument, updateDocument } = usePAGrantsStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mapInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxDoc, setLightboxDoc] = useState<PADocument | null>(null);
  const [lightboxFullUrl, setLightboxFullUrl] = useState<string>('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingTag, setPendingTag] = useState('pre_disaster');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [editingDoc, setEditingDoc] = useState<PADocument | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editTag, setEditTag] = useState('');
  const [phaseFilter, setPhaseFilter] = useState<string | null>(null);

  // Separate photos vs maps
  const allImages = documents.filter(d =>
    isImage(d.mimeType || '') || isImage(d.documentName || '')
  );
  const allPhotos = allImages.filter(d => (d.tags || '') !== 'maps_sketches');
  const maps = allImages.filter(d => (d.tags || '') === 'maps_sketches');

  // Apply phase filter
  const photos = phaseFilter ? allPhotos.filter(d => d.tags === phaseFilter) : allPhotos;

  // Phase counts for filter bar
  const phaseCounts = Object.fromEntries(
    PHOTO_PHASES.map(p => [p.value, allPhotos.filter(d => d.tags === p.value).length])
  );

  async function handleBulkUpload(files: File[], tag = 'pre_disaster', description = '') {
    if (files.length === 0) return;
    setUploading(true);
    setUploadCount(0);
    setUploadTotal(files.length);

    const FDoc = FIELDS.paDocuments;
    const user = useAuthStore.getState().user;
    let success = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      setUploadCount(i + 1);
      try {
        const [gridThumb, lightboxImg] = await Promise.all([
          createGridThumb(file),
          createLightboxImage(file),
        ]);
        const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.paDocuments);
        const notesVal = `|||DOCID|||${documentId}|||LIGHTBOX|||${lightboxImg}|||THUMB|||${gridThumb}`;
        const created = await createRecord<{ record_id: number }>(TABLES.paDocuments, {
          [FDoc.projectId]: projectId,
          [FDoc.organizationId]: organizationId,
          [FDoc.title]: file.name,
          [FDoc.documentType]: 'Photo',
          [FDoc.uploadedBy]: user?.email || '',
          [FDoc.tags]: tag,
          [FDoc.notes]: notesVal,
        });
        if (created?.record_id) {
          await updateRecord(TABLES.paDocuments, created.record_id, {
            [FDoc.file]: attachmentId,
          });
        }
        success++;
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }

    await fetchDocuments(Number(projectId));
    setUploading(false);
    notifications.show({
      title: 'Photos uploaded',
      message: `${success} of ${files.length} photo${files.length !== 1 ? 's' : ''} uploaded`,
      color: 'var(--color-success)',
    });
  }

  function onFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFiles(Array.from(e.target.files));
      setPendingTag(phaseFilter || 'pre_disaster');
      setUploadDescription('');
      setUploadModalOpen(true);
    }
    e.target.value = '';
  }

  function onMapSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFiles(Array.from(e.target.files));
      setPendingTag('maps_sketches');
      setUploadDescription('');
      setUploadModalOpen(true);
    }
    e.target.value = '';
  }

  async function confirmUpload() {
    if (pendingFiles.length === 0) return;
    setUploadModalOpen(false);
    await handleBulkUpload(pendingFiles, pendingTag, uploadDescription);
    setPendingFiles([]);
    setUploadDescription('');
  }

  async function saveEdit() {
    if (!editingDoc) return;
    try {
      await updateDocument(editingDoc.Rid, { description: editDescription, tags: editTag });
      setEditingDoc(null);
      notifications.show({ message: 'Photo updated', color: 'var(--color-success)' });
    } catch {
      notifications.show({ message: 'Failed to update', color: 'var(--color-error)' });
    }
  }

  function openLightbox(doc: PADocument) {
    setLightboxDoc(doc);
    setLightboxOpen(true);
    const lightbox = (doc as any).lightboxUrl || '';
    const thumb = doc.notes || '';
    setLightboxFullUrl(lightbox || thumb);
    if (!lightbox && doc.fileId) {
      getV2FileDownloadUrl(doc.fileId)
        .then(url => setLightboxFullUrl(url))
        .catch(() => {});
    }
  }

  return (
    <Stack gap="md">
      {/* Hidden inputs */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={onFileSelect} />
      <input type="file" ref={mapInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={onMapSelect} />

      {/* Upload zone */}
      <Paper p="lg" withBorder
        style={{ border: '2px dashed rgba(75,63,255,0.28)', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <Stack align="center" gap="xs">
          {uploading ? (
            <>
              <Loader size={32} />
              <Text size="sm" c="dimmed">Uploading {uploadCount} of {uploadTotal}...</Text>
            </>
          ) : (
            <>
              <IconPhoto size={32} color="var(--color-primary-700)" />
              <Text size="sm" c="dimmed">Click to upload photos — select multiple files at once</Text>
              <Text size="xs" c="dimmed">JPG, PNG, GIF, WebP accepted</Text>
            </>
          )}
        </Stack>
      </Paper>

      {/* Phase filter bar */}
      {allPhotos.length > 0 && (
        <Group gap="xs" justify="space-between">
          <Group gap="xs">
            <Button size="xs" variant={phaseFilter === null ? 'filled' : 'light'}
              color="var(--color-primary-700)" onClick={() => setPhaseFilter(null)}>
              All ({allPhotos.length})
            </Button>
            {PHOTO_PHASES.filter(p => (phaseCounts[p.value] || 0) > 0).map(p => (
              <Button key={p.value} size="xs"
                variant={phaseFilter === p.value ? 'filled' : 'light'}
                style={phaseFilter === p.value ? { background: p.color } : { color: p.color }}
                onClick={() => setPhaseFilter(phaseFilter === p.value ? null : p.value)}>
                {p.label} ({phaseCounts[p.value]})
              </Button>
            ))}
          </Group>
          <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
            onClick={() => fileInputRef.current?.click()} loading={uploading}>
            Add More
          </Button>
        </Group>
      )}

      {/* Photo grid */}
      {photos.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <Stack align="center" gap="sm">
            <IconPhoto size={48} color="#939598" />
            <Text c="dimmed">
              {phaseFilter
                ? `No ${PHASE_MAP[phaseFilter]?.label || phaseFilter} photos.`
                : 'No photos uploaded for this project yet.'}
            </Text>
            <Button size="sm" variant="light" leftSection={<IconUpload size={14} />}
              onClick={() => fileInputRef.current?.click()}>
              Upload Photos
            </Button>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="sm">
          {photos.map(doc => {
            const url = doc.notes?.startsWith('data:') ? doc.notes : '';
            return (
              <Paper key={doc.Rid} withBorder radius="md" style={{ overflow: 'hidden', position: 'relative' }}>
                <div
                  style={{ aspectRatio: '4/3', background: '#f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                  onClick={() => openLightbox(doc)}
                >
                  <Image src={url} alt={doc.documentName} fit="cover" h="100%" w="100%"
                    fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f0f0f0' width='100' height='100'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%23999' font-size='12'%3EPhoto%3C/text%3E%3C/svg%3E"
                  />
                </div>
                {/* Phase badge overlay */}
                {doc.tags && PHASE_MAP[doc.tags] && (
                  <div style={{ position: 'absolute', top: 4, left: 4 }}>
                    <PhaseBadge tag={doc.tags} />
                  </div>
                )}
                <Group justify="space-between" p={6}>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={500} truncate>{doc.documentName}</Text>
                    {doc.description && <Text size="xs" c="dimmed" truncate>{doc.description}</Text>}
                    <Text size="xs" c="dimmed">{fmtDateTime(doc.uploadDate)}</Text>
                  </Stack>
                  <Group gap={2}>
                    <ActionIcon size="xs" variant="subtle" onClick={() => openLightbox(doc)}>
                      <IconMaximize size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" onClick={(e) => {
                      e.stopPropagation();
                      setEditingDoc(doc);
                      setEditDescription(doc.description || '');
                      setEditTag(doc.tags || 'damage_photos');
                    }}>
                      <IconEdit size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" color="var(--color-error)"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!confirm('Delete this photo?')) return;
                        try {
                          await deleteDocument(doc.Rid);
                          notifications.show({ message: 'Photo deleted', color: '#939598' });
                        } catch (err) {
                          notifications.show({ message: `Delete failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
                        }
                      }}>
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {/* ── Maps & Sketches ──────────────────────────────── */}
      <Paper p="sm" withBorder radius="sm" mt="lg">
        <Group gap="xs" mb="sm">
          <IconMap size={18} color="var(--color-primary-700)" />
          <Text size="sm" fw={600}>Maps & Sketches</Text>
          <Text size="xs" c="dimmed">(FEMA 009-0-91C)</Text>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          Upload site maps, damage sketches, facility drawings, or aerial images documenting the project location and extent of damage.
        </Text>
        {maps.length === 0 ? (
          <Paper p="md" withBorder
            style={{ border: '2px dashed rgba(75,63,255,0.28)', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
            onClick={() => !uploading && mapInputRef.current?.click()}
          >
            <Stack align="center" gap="xs">
              <IconMap size={28} color="#7589FF" />
              <Text size="sm" c="dimmed">Click to upload maps, sketches, or drawings</Text>
            </Stack>
          </Paper>
        ) : (
          <>
            <Group justify="space-between" mb="xs">
              <Text size="xs" c="dimmed">{maps.length} map{maps.length !== 1 ? 's' : ''} / sketch{maps.length !== 1 ? 'es' : ''}</Text>
              <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                onClick={() => mapInputRef.current?.click()} loading={uploading}>Add More</Button>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
              {maps.map(doc => {
                const url = doc.notes?.startsWith('data:') ? doc.notes : '';
                return (
                  <Paper key={doc.Rid} withBorder radius="md" style={{ overflow: 'hidden' }}>
                    <div style={{ aspectRatio: '4/3', background: '#f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                      onClick={() => openLightbox(doc)}>
                      <Image src={url} alt={doc.documentName} fit="cover" h="100%" w="100%"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f0f0f0' width='100' height='100'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%23999' font-size='12'%3EMap%3C/text%3E%3C/svg%3E" />
                    </div>
                    <Group justify="space-between" p={6}>
                      <Text size="xs" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{doc.documentName}</Text>
                      <Group gap={2}>
                        <ActionIcon size="xs" variant="subtle" onClick={() => openLightbox(doc)}><IconMaximize size={12} /></ActionIcon>
                        <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={async (e) => {
                          e.stopPropagation();
                          try { await deleteDocument(doc.Rid); notifications.show({ message: 'Map deleted', color: '#939598' }); }
                          catch (err) { notifications.show({ message: `Delete failed: ${err instanceof Error ? err.message : 'Unknown'}`, color: 'var(--color-error)' }); }
                        }}><IconTrash size={12} /></ActionIcon>
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </Paper>

      {/* Upload modal */}
      <Modal opened={uploadModalOpen} onClose={() => { setUploadModalOpen(false); setPendingFiles([]); }}
        title={`Upload ${pendingFiles.length} ${pendingTag === 'maps_sketches' ? 'map(s)' : 'photo(s)'}`}
        size="sm" centered>
        <Stack gap="md">
          <Text size="sm" c="dimmed">{pendingFiles.map(f => f.name).join(', ')}</Text>
          {pendingTag !== 'maps_sketches' && (
            <Select
              label="Photo Phase"
              description="What stage of the project do these photos document?"
              data={PHASE_SELECT_OPTIONS}
              value={pendingTag}
              onChange={v => setPendingTag(v || 'pre_disaster')}
            />
          )}
          <Textarea
            label="Description"
            placeholder="Describe the damage, location, or context shown in these photos..."
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            minRows={2}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => { setUploadModalOpen(false); setPendingFiles([]); }}>Cancel</Button>
            <Button onClick={confirmUpload} leftSection={<IconUpload size={14} />}>Upload</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit modal */}
      <Modal opened={!!editingDoc} onClose={() => setEditingDoc(null)}
        title={`Edit — ${editingDoc?.documentName || ''}`} size="sm" centered>
        <Stack gap="md">
          <Select
            label="Photo Phase"
            data={PHASE_SELECT_OPTIONS}
            value={editTag}
            onChange={v => setEditTag(v || 'damage_photos')}
          />
          <Textarea
            label="Description"
            placeholder="Describe the damage, location, or context..."
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            minRows={2}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setEditingDoc(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Lightbox */}
      <Modal opened={lightboxOpen} onClose={() => { setLightboxOpen(false); setLightboxFullUrl(''); }}
        title={lightboxDoc?.documentName || 'Photo'} size="xl" centered>
        {lightboxDoc && (
          <Stack gap="sm">
            <div style={{ background: '#0f172a', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              {lightboxFullUrl ? (
                <img src={lightboxFullUrl} alt={lightboxDoc.documentName}
                  style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain' }}
                  onError={() => {
                    const thumb = lightboxDoc.notes || '';
                    if (thumb && lightboxFullUrl !== thumb) setLightboxFullUrl(thumb);
                  }}
                />
              ) : (
                <div style={{ padding: 48 }}><Loader size={32} color="white" /></div>
              )}
            </div>
            <Group gap="sm">
              {lightboxDoc.tags && PHASE_MAP[lightboxDoc.tags] && <PhaseBadge tag={lightboxDoc.tags} />}
              <Text size="xs" c="dimmed">
                {fmtDateTime(lightboxDoc.uploadDate)} {lightboxDoc.uploadedBy ? `by ${lightboxDoc.uploadedBy}` : ''}
                {lightboxDoc.fileSize ? ` | ${(parseInt(lightboxDoc.fileSize) / 1024).toFixed(0)} KB` : ''}
              </Text>
            </Group>
            {lightboxDoc.description && <Text size="sm" c="dimmed">{lightboxDoc.description}</Text>}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
